import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { LoginRequest } from '../types';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'CLIENT' | 'USER'>('CLIENT');
  const isUserRole = role === 'USER';
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [sessionReplaced, setSessionReplaced] = useState(false);

  // Check if user was kicked due to session replacement
  React.useEffect(() => {
    const replaced = localStorage.getItem('session_replaced');
    if (replaced) {
      localStorage.removeItem('session_replaced');
      setSessionReplaced(true);
    }
  }, []);

  const handleRoleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newRole: 'CLIENT' | 'USER' | null
  ) => {
    if (newRole !== null) {
      setRole(newRole);
    }
  };

  const validateForm = (): boolean => {
    if (!email.trim()) {
      setError(isUserRole ? t('auth.usernameRequired') : t('auth.emailRequired'));
      setShowError(true);
      return false;
    }
    if (!isUserRole && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('auth.emailInvalid'));
      setShowError(true);
      return false;
    }
    if (!password.trim()) {
      setError(t('auth.passwordRequired'));
      setShowError(true);
      return false;
    }
    if (password.length < 6) {
      setError(t('auth.passwordMin'));
      setShowError(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const credentials: LoginRequest = { email, password, role };
      await login(credentials);

      // Explicitly trigger Chrome credential save for SPA login
      if (window.PasswordCredential) {
        try {
          const cred = new window.PasswordCredential({ id: email, password });
          await navigator.credentials.store(cred);
        } catch (_) { /* ignore if credential API fails */ }
      }

      switch (role) {
        case 'CLIENT':
          navigate('/client/dashboard');
          break;
        case 'USER':
          navigate('/user/desktop');
          break;
      }
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        t('auth.loginFailed');
      setError(message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0d1b3e 0%, #1a237e 40%, #0d47a1 70%, #1565c0 100%)',
        p: 2,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
            py: 4,
            px: 3,
            textAlign: 'center',
            borderRadius: '12px 12px 0 0',
          }}
        >
          <CloudIcon sx={{ fontSize: 48, color: '#ffffff', mb: 1 }} />
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              letterSpacing: '1px',
            }}
          >
            {t('app.name')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              mt: 0.5,
            }}
          >
            {t('app.subtitle')}
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit} autoComplete="on">
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1.5,
                fontWeight: 600,
                color: '#37474f',
                textAlign: 'center',
              }}
            >
              {t('auth.selectRole')}
            </Typography>
            <ToggleButtonGroup
              value={role}
              exclusive
              onChange={handleRoleChange}
              fullWidth
              sx={{
                mb: 3,
                '& .MuiToggleButton-root': {
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  borderColor: '#e0e0e0',
                  '&.Mui-selected': {
                    backgroundColor: '#1a237e',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#283593',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="CLIENT">
                <BusinessIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('auth.client')}
              </ToggleButton>
              <ToggleButton value="USER">
                <PersonIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('auth.user')}
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              fullWidth
              label={isUserRole ? t('auth.username') : t('auth.emailAddress')}
              type={isUserRole ? 'text' : 'email'}
              name={isUserRole ? 'username' : 'email'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isUserRole ? <PersonIcon sx={{ color: '#9e9e9e' }} /> : <EmailIcon sx={{ color: '#9e9e9e' }} />}
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              autoComplete={isUserRole ? 'username' : 'username email'}
            />

            <TextField
              fullWidth
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                boxShadow: '0 4px 15px rgba(26,35,126,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #283593 0%, #1565c0 100%)',
                  boxShadow: '0 6px 20px rgba(26,35,126,0.5)',
                },
                '&:disabled': {
                  background: '#bdbdbd',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                t('auth.signIn')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={sessionReplaced}
        autoHideDuration={8000}
        onClose={() => setSessionReplaced(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSessionReplaced(false)}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('auth.sessionReplaced')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
